import {useCallback, useEffect, useMemo, useState} from 'react'
import {useOrderProcessFormContext} from '../../form/useOrderProcessFormContext'
import {SetSlotAvailabilityOptions, SlotAvailability, SlotManagementContextProps} from './SlotManagementContext'
import {SlotManagementConfig} from '../types'

/**
 * Manages slot availability state synchronized with the order process form.
 *
 * This hook automatically tracks slot availability for all dateTimes in the form,
 * maintaining synchronization as dateTimes are added, removed, or modified.
 *
 * **Synchronization behavior:**
 * - Initializes with all slots as unavailable (isAvailable: false)
 * - When form dateTimes change:
 *   - Preserves availability status for matching dateTimes
 *   - Creates new slots (unavailable) for new dateTimes
 *   - Removes slots for deleted dateTimes
 *   - Maintains same array order as form's dateTime field
 *
 * **Use cases:**
 * - Order intake: Track availability for multiple delivery windows
 * - Order management: Track availability for single delivery time change
 * - Slot-based auto-approval: Enable fields when slots become available
 *
 * @param config - Configuration for slot management (feature toggle, org unit, site, order ID)
 * @param hooks - Strategy hooks for slot availability and validation
 * @returns Slot management state, update functions, config, and strategy hooks
 *
 * @example
 * ```tsx
 * const { slotAvailabilities, setSlotAvailability, config, hooks } = useSlotManagementState(config, hooks)
 *
 * // Check if first slot is available
 * const isFirstSlotAvailable = slotAvailabilities[0]?.isAvailable
 *
 * // Update availability by dateTime (to be called after slot validation)
 * setSlotAvailability({
 *   dateTime: '2025-10-28T10:00:00Z',
 *   isAvailable: true
 * })
 *
 * // Update availability by index (p.ex. to be called after slot validation)
 * setSlotAvailability({
 *   dateTimeIndex: 0,
 *   isAvailable: false
 * })
 * ```
 */
export const useSlotManagementState = (
  config: SlotManagementConfig,
  hooks: SlotManagementContextProps['hooks']
) => {
  // Watch the form's dateTime field to stay synchronized
  const {watch} = useOrderProcessFormContext()
  const dateTimes = watch('dateTime')

  // Initialize slot availabilities for all current dateTimes
  // Default state: all slots unavailable until explicitly marked available
  const [slotAvailabilities, setSlotAvailabilities] = useState<SlotAvailability[]>(() =>
    dateTimes.map((dateTime) => ({
      dateTime,
      isAvailable: false
    }))
  )

  // Synchronize slot availabilities whenever form dateTimes change
  useEffect(() => {
    setSlotAvailabilities((prevSlotAvailabilities) => {
      return dateTimes.map((dateTime) => {
        // Search for existing slot by dateTime string comparison
        const existingSlot = prevSlotAvailabilities.find((slot) => slot.dateTime === dateTime)

        // Preserve existing availability status or default to unavailable
        return (
          existingSlot || {
            dateTime,
            isAvailable: false
          }
        )
      })
    })
  }, [dateTimes])

  /**
   * Updates the availability status of a specific slot.
   *
   * Supports two ways to identify the slot:
   * - By `dateTime` string: Finds the slot matching the dateTime
   * - By `dateTimeIndex` number: Uses array index directly (faster)
   *
   * @param params - Slot identifier and new availability status
   */
  const setSlotAvailability = useCallback((params: SetSlotAvailabilityOptions) => {
    setSlotAvailabilities((prev) => {
      let index: number

      if ('dateTime' in params) {
        // Search mode: Find slot index by dateTime string comparison
        index = prev.findIndex((slot) => slot.dateTime === params.dateTime)
      } else {
        // Direct mode: Use provided index (faster, requires caller knows index)
        index = params.dateTimeIndex
      }

      // Validation: Ensure index is valid
      if (index === -1 || index >= prev.length || index < 0) {
        // Slot not found or invalid index - return unchanged state
        return prev
      }

      // Create immutable update: new array with updated slot
      const updated = [...prev]
      updated[index] = {
        ...updated[index],
        isAvailable: params.isAvailable
      }

      return updated
    })
  }, [])

  return useMemo(
    () => ({
      slotAvailabilities,
      setSlotAvailability,
      // TBC if we need some safety here and expect list of slots instead of setting state directly
      setSlotAvailabilities,
      config,
      hooks
    }),
    [slotAvailabilities, setSlotAvailability, setSlotAvailabilities, config, hooks]
  )
}
